import { IDevice, IGateway } from "../types";
import { TooltipPosition } from "./tooltipPositioning";
import { convertRelationalToPixelRadius, MapDimensions } from "./radiusUtils";

interface Box {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface PositionedDevice extends IDevice {
    displayPosition?: { x: number; y: number };
    needsLeaderLine?: boolean;
    tooltipPosition?: TooltipPosition;
}

/**
 * Calculate the bounding box for a name box at a given position
 */
function calculateNameBoxBounds(
    x: number,
    y: number,
    deviceName: string
): Box {
    // Calculate width based on text length (same as in DeviceMarker)
    const charWidth = 8;
    const padding = 16;
    const minWidth = 60;
    const maxWidth = 200;
    const width = Math.min(
        Math.max(deviceName.length * charWidth + padding, minWidth),
        maxWidth
    );
    const height = 28;

    const box = {
        x: x - width / 2,
        y: y - height / 2,
        width,
        height,
    };

    // console.log(`📦 Box for "${deviceName}" at (${x}, ${y}): x=${box.x.toFixed(1)}, y=${box.y.toFixed(1)}, w=${box.width}, h=${box.height}`);

    return box;
}

/**
 * Check if two boxes overlap
 */
function boxesOverlap(box1: Box, box2: Box): boolean {
    const overlaps = !(
        box1.x + box1.width < box2.x ||
        box2.x + box2.width < box1.x ||
        box1.y + box1.height < box2.y ||
        box2.y + box2.height < box1.y
    );

    // if (overlaps) {
    //     console.log(`💥 OVERLAP DETECTED: Box1(${box1.x.toFixed(0)},${box1.y.toFixed(0)},${box1.width}x${box1.height}) overlaps Box2(${box2.x.toFixed(0)},${box2.y.toFixed(0)},${box2.width}x${box2.height})`);
    // }

    return overlaps;
}

/**
 * Check if a position is within the gateway coverage radius
 */
function isWithinGatewayCoverage(
    devicePos: { x: number; y: number },
    gatewayPos: { x: number; y: number },
    radius: number,
    boxWidth: number,
    boxHeight: number
): boolean {
    // Check if all corners of the box are within the gateway radius
    const corners = [
        { x: devicePos.x - boxWidth / 2, y: devicePos.y - boxHeight / 2 }, // top-left
        { x: devicePos.x + boxWidth / 2, y: devicePos.y - boxHeight / 2 }, // top-right
        { x: devicePos.x - boxWidth / 2, y: devicePos.y + boxHeight / 2 }, // bottom-left
        { x: devicePos.x + boxWidth / 2, y: devicePos.y + boxHeight / 2 }, // bottom-right
    ];

    return corners.every((corner) => {
        const distance = Math.sqrt(
            Math.pow(corner.x - gatewayPos.x, 2) +
            Math.pow(corner.y - gatewayPos.y, 2)
        );
        return distance <= radius;
    });
}

/**
 * Find a valid position for a name box that doesn't overlap with others
 * and stays within the gateway coverage radius
 */
function findValidPosition(
    device: IDevice,
    gateway: IGateway,
    occupiedBoxes: Box[],
    deviceName: string,
    mapDimensions: MapDimensions
): { x: number; y: number } | null {
    if (!device.position || !gateway.position) {
        return null;
    }

    const originalPos = device.position;
    const gatewayPos = gateway.position;

    // Convert relational radius (0-100) to pixel radius using the same method as GatewayMarker
    const relationalRadius = gateway.viewRadius ?? gateway.radius ?? 25;
    const pixelRadius = convertRelationalToPixelRadius(relationalRadius, mapDimensions);

    // Calculate box dimensions
    const box = calculateNameBoxBounds(originalPos.x, originalPos.y, deviceName);

    // console.log(`🌐 Gateway for ${deviceName}: pos=(${gatewayPos.x}, ${gatewayPos.y}), relationalRadius=${relationalRadius}, pixelRadius=${pixelRadius}`);

    // Try the original position first - check for overlaps AND gateway coverage
    // console.log(`🔎 Checking ${deviceName} against ${occupiedBoxes.length} occupied boxes`);
    const hasOverlap = occupiedBoxes.some((occupied) => {
        const overlaps = boxesOverlap(box, occupied);
        // if (overlaps) {
        //     console.log(`  ✓ Found overlap with occupied box at (${occupied.x.toFixed(0)}, ${occupied.y.toFixed(0)})`);
        // }
        return overlaps;
    });

    // Check if original position is within gateway coverage
    const isWithinCoverage = isWithinGatewayCoverage(
        originalPos,
        gatewayPos,
        pixelRadius,
        box.width,
        box.height
    );

    if (!hasOverlap && isWithinCoverage) {
        // console.log(`✅ Device ${deviceName} at (${originalPos.x}, ${originalPos.y}) has no overlaps and is within coverage - using original position`);
        return originalPos;
    }

    if (hasOverlap) {
        // console.log(`⚠️ Device ${deviceName} at (${originalPos.x}, ${originalPos.y}) overlaps with existing box - searching for new position`);
    } else if (!isWithinCoverage) {
        // console.log(`⚠️ Device ${deviceName} at (${originalPos.x}, ${originalPos.y}) is outside gateway coverage - searching for new position`);
    }

    // Search in a spiral pattern around the original position
    const step = 10; // pixels to move in each step
    const maxAttempts = 50; // limit search to prevent infinite loops

    // First pass: try to find positions INSIDE the gateway
    for (let attempt = 1; attempt < maxAttempts; attempt++) {
        const angle = attempt * 0.5; // spiral angle
        const distance = attempt * step;

        // Try 8 directions at this distance
        for (let dir = 0; dir < 8; dir++) {
            const testAngle = angle + (dir * Math.PI) / 4;
            const testX = originalPos.x + distance * Math.cos(testAngle);
            const testY = originalPos.y + distance * Math.sin(testAngle);

            // Check for overlaps AND gateway coverage
            const testBox = calculateNameBoxBounds(testX, testY, deviceName);
            const testPos = { x: testX, y: testY };

            const hasTestOverlap = occupiedBoxes.some((occupied) =>
                boxesOverlap(testBox, occupied)
            );

            const isTestWithinCoverage = isWithinGatewayCoverage(
                testPos,
                gatewayPos,
                pixelRadius,
                testBox.width,
                testBox.height
            );

            // Prioritize positions inside the gateway
            if (!hasTestOverlap && isTestWithinCoverage) {
                // console.log(`✨ Found valid position INSIDE gateway for ${deviceName} at (${testX.toFixed(0)}, ${testY.toFixed(0)}) after ${attempt} attempts`);
                return testPos;
            }
        }
    }

    // Second pass: if no position found inside gateway, try positions outside as last resort
    // console.log(`⚠️ No position found inside gateway for ${deviceName}, trying outside positions`);
    for (let attempt = 1; attempt < maxAttempts; attempt++) {
        const angle = attempt * 0.5;
        const distance = attempt * step;

        for (let dir = 0; dir < 8; dir++) {
            const testAngle = angle + (dir * Math.PI) / 4;
            const testX = originalPos.x + distance * Math.cos(testAngle);
            const testY = originalPos.y + distance * Math.sin(testAngle);

            const testBox = calculateNameBoxBounds(testX, testY, deviceName);

            // Only check for overlaps, allow positions outside gateway
            if (!occupiedBoxes.some((occupied) => boxesOverlap(testBox, occupied))) {
                // console.log(`✨ Found valid position OUTSIDE gateway for ${deviceName} at (${testX.toFixed(0)}, ${testY.toFixed(0)}) after ${attempt} attempts`);
                return { x: testX, y: testY };
            }
        }
    }

    // If no valid position found after max attempts, return original position
    // console.log(`❌ Could not find valid position for ${deviceName} after ${maxAttempts} attempts - using original position`);
    return originalPos;
}

/**
 * Resolve collisions for all devices, keeping them within gateway coverage
 */
export function resolveNameBoxCollisions(
    devices: IDevice[],
    gateways: Record<string, IGateway>,
    mapDimensions: MapDimensions
): PositionedDevice[] {
    // console.log('🔍 Collision detection running for', devices.length, 'devices');
    const positionedDevices: PositionedDevice[] = [];
    const occupiedBoxes: Box[] = [];

    // Sort devices by priority (you could add custom priority logic here)
    // For now, we'll process them in order
    const sortedDevices = [...devices];

    for (const device of sortedDevices) {
        // Get the device's gateway
        const gateway = device.strongestGateway
            ? gateways[device.strongestGateway]
            : null;

        if (!gateway || !device.position) {
            // If no gateway or position, keep original
            positionedDevices.push(device);
            continue;
        }

        // Get device name
        const deviceName =
            device.customSettings?.displayName || device.name || device.mac;

        // Find a valid position
        const validPosition = findValidPosition(
            device,
            gateway,
            occupiedBoxes,
            deviceName,
            mapDimensions
        );

        if (validPosition) {
            const needsLeaderLine =
                validPosition.x !== device.position.x ||
                validPosition.y !== device.position.y;

            // if (needsLeaderLine) {
            //     console.log(`📍 Device ${deviceName} repositioned from (${device.position.x}, ${device.position.y}) to (${validPosition.x}, ${validPosition.y})`);
            // }

            positionedDevices.push({
                ...device,
                displayPosition: validPosition,
                needsLeaderLine,
            });

            // Add this box to occupied boxes
            const box = calculateNameBoxBounds(
                validPosition.x,
                validPosition.y,
                deviceName
            );
            occupiedBoxes.push(box);
        } else {
            // Fallback to original position
            positionedDevices.push(device);
        }
    }

    return positionedDevices;
}
